import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const FLOWERS = ['🌼', '🌷', '🌻', '🌸', '🪻', '🌺'];

export default function CriteriaManagerDialog({ open, onClose, scopeLabel, loadCriteria, createCriterion, updateCriterion, deleteCriterion, onChanged, canManageCriterion = () => true }) {
  const loadCriteriaRef = useRef(loadCriteria);
  loadCriteriaRef.current = loadCriteria;
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [deleteRow, setDeleteRow] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await loadCriteriaRef.current();
      setCriteria(Array.isArray(result) ? result : result?.items || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Kriterler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const finishChange = async () => {
    await refresh();
    await onChanged();
  };

  const add = async () => {
    if (!newName.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      await createCriterion({ name: newName.trim(), icon: null, sort_order: 99 });
      setNewName('');
      await finishChange();
    } catch (err) {
      setError(err.message || 'Kriter eklenemedi.');
    } finally {
      setBusy(false);
    }
  };

  const save = async (row) => {
    if (!editName.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      await updateCriterion(row.id, { name: editName.trim(), sort_order: row.sort_order ?? 99 });
      setEditingId(null);
      await finishChange();
    } catch (err) {
      setError(err.message || 'Kriter güncellenemedi.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleteRow || busy) return;
    setBusy(true);
    setError('');
    try {
      await deleteCriterion(deleteRow.id);
      setDeleteRow(null);
      await finishChange();
    } catch (err) {
      setError(err.message || 'Kriter silinemedi.');
    } finally {
      setBusy(false);
    }
  };

  return <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md" aria-labelledby="criteria-manager-title" PaperProps={{ sx: { borderRadius: 4 } }}>
    <DialogTitle id="criteria-manager-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 900, color: '#2e7d32' }}>
      Kriter Güncelle
      <IconButton onClick={onClose} disabled={busy} aria-label="Kriter penceresini kapat"><CloseIcon /></IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <Typography sx={{ color: 'text.secondary', mb: 2 }}>Kapsam: {scopeLabel}. Silinen kriterler yeni yıldız vermede görünmez; geçmiş yıldızlar korunur.</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Yeni Kriter Ekle</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField label="Kriter adı" value={newName} onChange={(event) => setNewName(event.target.value)} fullWidth size="small" inputProps={{ maxLength: 128 }} />
          <Button variant="contained" onClick={add} disabled={!newName.trim() || busy} sx={{ whiteSpace: 'nowrap' }}>Ekle</Button>
        </Stack>
      </Paper>
      <Typography sx={{ fontWeight: 800, mb: 1 }}>Kriterler</Typography>
      {loading && <CircularProgress size={24} />}
      {!loading && criteria.length === 0 && <Typography sx={{ color: 'text.secondary' }}>Kriter bulunamadı.</Typography>}
      <Stack spacing={1}>
        {!loading && criteria.map((row, index) => {
          const manageable = canManageCriterion(row);
          return <Paper key={row.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {editingId === row.id ? <>
              <TextField aria-label="Kriter adı" value={editName} onChange={(event) => setEditName(event.target.value)} size="small" sx={{ flex: 1, minWidth: 170 }} />
              <Button onClick={() => save(row)} disabled={!editName.trim() || busy}>Kaydet</Button>
              <Button onClick={() => setEditingId(null)} disabled={busy}>Vazgeç</Button>
            </> : <>
              <Typography sx={{ fontSize: 22 }}>{FLOWERS[index % FLOWERS.length]}</Typography>
              <Box sx={{ flex: 1, minWidth: 150 }}>
                <Typography sx={{ fontWeight: 800 }}>{row.name}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{row.usage_count || 0} kez kullanıldı</Typography>
              </Box>
              <Tooltip title={manageable ? 'Düzenle' : 'Düzenlenemez'}><span><IconButton aria-label={`${row.name} düzenle`} onClick={() => { setEditingId(row.id); setEditName(row.name); }} disabled={!manageable || busy}><EditIcon /></IconButton></span></Tooltip>
              <Tooltip title={manageable ? 'Sil' : 'Silinemez'}><span><IconButton aria-label={`${row.name} sil`} onClick={() => setDeleteRow(row)} disabled={!manageable || busy} color="error"><DeleteIcon /></IconButton></span></Tooltip>
            </>}
          </Paper>;
        })}
      </Stack>
      {deleteRow && <Alert severity="warning" sx={{ mt: 2 }} action={<Box sx={{ display: 'flex', gap: .5 }}><Button color="inherit" size="small" onClick={() => setDeleteRow(null)} disabled={busy}>Vazgeç</Button><Button color="error" size="small" onClick={remove} disabled={busy}>Sil</Button></Box>}>
        {deleteRow.name} bu sınıftan kaldırılsın mı? Geçmiş yıldız kayıtları korunur.
      </Alert>}
    </DialogContent>
    <DialogActions><Button onClick={onClose} disabled={busy}>Kapat</Button></DialogActions>
  </Dialog>;
}
